import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { EntityReferenceView } from './EntityReferenceView';

export type EntityType =
  | 'convention'
  | 'completionReport'
  | 'postMortem'
  | 'coAction'
  | 'codeReview'
  | 'document'
  | 'plan'
  | 'planTask'
  | 'skill'
  | 'GITHUB_ISSUE'
  | 'GITHUB_PR'
  | 'GITLAB_ISSUE'
  | 'GITLAB_MERGE_REQUEST'
  | 'BITBUCKET_ISSUE'
  | 'BITBUCKET_PR'
  | 'LINEAR_ISSUE'
  | 'SENTRY_ISSUE'
  | 'branch';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    entityReference: {
      insertEntityReference: (attrs: {
        entityType: EntityType;
        entityId: string;
        label: string;
        path?: string;
        htmlUrl?: string;
      }) => ReturnType;
    };
  }
}

export const EntityReference = Node.create({
  name: 'entityReference',

  group: 'inline',

  inline: true,

  atom: true,

  selectable: true,

  draggable: false,

  addAttributes() {
    return {
      entityType: {
        default: 'convention' as EntityType,
        parseHTML: (element) => element.getAttribute('data-entity-type') ?? 'convention',
        renderHTML: (attributes) => ({
          'data-entity-type': attributes.entityType as string,
        }),
      },
      entityId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-entity-id') ?? '',
        renderHTML: (attributes) => ({
          'data-entity-id': attributes.entityId as string,
        }),
      },
      label: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-label') ?? '',
        renderHTML: (attributes) => ({
          'data-label': attributes.label as string,
        }),
      },
      path: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-path') ?? '',
        renderHTML: (attributes) => ({
          'data-path': attributes.path as string,
        }),
      },
      htmlUrl: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-html-url') ?? '',
        renderHTML: (attributes) => ({
          'data-html-url': attributes.htmlUrl as string,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-entity-reference]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-entity-reference': '',
        class: 'entity-reference',
      }),
      HTMLAttributes['data-label'] as string,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(EntityReferenceView, {
      as: 'span',
      className: 'inline',
    });
  },

  addCommands() {
    return {
      insertEntityReference:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          });
        },
    };
  },
});
